import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchCopilotPreviewV2 } from '../api/CopilotPreviewV2Api';
import { FAILED, PENDING, SUCCEEDED, UNINITIALIZED } from '../types/requestStatus';
import { getLocale } from 'I18n/utils/internal/light/intlFormatters';
import { getMapV1DataToV2 } from '../components/CopilotPreview/internalV2/utils/mapV1DataToV2';
import { getIsPastRefreshThreshold } from '../components/CopilotPreview/internal/utils/RefreshButton';
export const useGetCopilotPreview = (previewKey, previewSource, copilotAction, numHoursTilSummaryStale, summaryType, useSimplifiedPreviews) => {
  const [requestStatus, setRequestStatus] = useState(UNINITIALIZED);
  const [preview, setPreview] = useState(null);
  const [isHipaaPortal, setIsHipaaPortal] = useState(false);
  const [error, setError] = useState(null);

  // Step 1: Memoize the mapped data
  const mappedData = useMemo(() => getMapV1DataToV2(copilotAction, summaryType), [copilotAction, summaryType]);

  // Step 2: Stable requestData
  const requestData = useMemo(() => {
    return {
      previewKey,
      previewSource,
      message: mappedData.message,
      summaryType: mappedData.summaryType,
      ticketThreadId: mappedData.ticketThreadId,
      locale: getLocale(),
      context: Object.assign({}, mappedData.context),
      propertyFilters: mappedData === null || mappedData === void 0 ? void 0 : mappedData.propertyFilters,
      summaryFormat: mappedData === null || mappedData === void 0 ? void 0 : mappedData.summaryFormat
    };
  }, [previewKey, previewSource, mappedData]);
  const resetPreview = useCallback(() => {
    setPreview(null);
    setError(null);
  }, []);

  // Step 3: Stable fetcher
  const handleFetchPreview = useCallback((shouldForceGeneration = false, isUserInitiated = false) => {
    resetPreview();
    setRequestStatus(PENDING);
    fetchCopilotPreviewV2(Object.assign({}, requestData, {
      summaryType: requestData.summaryType,
      shouldForceGeneration,
      useSimplifiedPreviews: useSimplifiedPreviews !== null && useSimplifiedPreviews !== void 0 ? useSimplifiedPreviews : false,
      isUserInitiated
    })).then(result => {
      if (result.type === 'rpcError' && result.rpcError.type === 'HIPAA_PORTAL_ACCESS_DENIED') {
        setRequestStatus(FAILED);
        setIsHipaaPortal(true);
        return;
      }
      if (result.type === 'rpcError') {
        setError(new Error(`RPC Error: ${JSON.stringify(result.rpcError)}`));
        setRequestStatus(FAILED);
        return;
      }
      const response = result.data;
      let isStale = false;
      if (useSimplifiedPreviews && response.result && 'createdAt' in response.result) {
        isStale = getIsPastRefreshThreshold({
          date: response.result.createdAt,
          threshold: numHoursTilSummaryStale
        });
      } else if (response.result && 'messages' in response.result) {
        isStale = getIsPastRefreshThreshold({
          date: response.result.messages[0].timestamp,
          threshold: numHoursTilSummaryStale
        });
      }
      if (!shouldForceGeneration && isStale) {
        handleFetchPreview(true, false);
      } else {
        setPreview(response);
        setRequestStatus(SUCCEEDED);
      }
    }).catch(e => {
      setError(e);
      setRequestStatus(FAILED);
    });
  }, [resetPreview, requestData, useSimplifiedPreviews, numHoursTilSummaryStale]);

  // Step 4: One-time fire
  useEffect(() => {
    if (requestStatus === UNINITIALIZED) {
      handleFetchPreview();
    }
  }, [handleFetchPreview, requestStatus]);
  return {
    previewResult: preview === null || preview === void 0 ? void 0 : preview.result,
    requestStatus,
    error,
    isHipaaPortal,
    refresh: () => handleFetchPreview(true, true)
  };
};